import {
  skipToken,
  useApolloClient,
  useMutation,
  useQuery,
  useSuspenseQuery,
} from "@apollo/client";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Alert,
  BreadcrumbArray,
  Button,
  Card,
  CashflowUpload,
  CashManagementChecklist,
  CashManagementStrategyForm,
  type CashManagementStrategyFormHandle,
  CashManagementStrategyView,
  type ChecklistItem,
  type ChecklistTypename,
  cn,
  DateUI,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Icon,
  Input,
  PrimaryFullScreenSpinner,
  PrimarySpinner,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  StatusUI,
  Textarea,
  Txt,
  toast,
} from "@repo/ui";
import { NotFoundError, prettyPrintEnum } from "@repo/utils";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { DateTime } from "luxon";
import { Suspense, useEffect, useRef, useState } from "react";
import { PiBuildingOffice } from "react-icons/pi";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { graphql } from "~/gql/client";
import type { CashManagementStrategyChecklistItemStatus } from "~/gql/client/graphql";
import { opsHead, useOpsTitle } from "~/head";

const CHECKLIST_GROUPS: Array<{ typename: ChecklistTypename; title: string }> =
  [
    {
      typename: "AccountCashManagementStrategyChecklistItem",
      title: "Account changes",
    },
    {
      typename: "TransferCashManagementStrategyChecklistItem",
      title: "Transfers & rebalances",
    },
    { typename: "RuleCashManagementStrategyChecklistItem", title: "Rules" },
  ];

function normaliseChecklistTags(value: string) {
  return value
    .split(",")
    .map((tag) => tag.trim())
    .filter((tag) => tag.length > 0);
}

export const Route = createFileRoute("/__auth/cash-management-strategy_/$id")({
  head: () => opsHead("Cash Management Strategy"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

const cashManagementStrategyDetailDocument = graphql(`
  query AdminCashManagementStrategyDetail($id: ID!) {
    node(id: $id) {
      ... on CashManagementStrategy {
        id
        shortRef
        createdAt
        updatedAt
        status
        generationStartedAt
        generationFinishedAt
        generatorStage
        mdx
        organisation {
          id
          name
          ...CashManagementOrg
        }
        checklist {
          id
          status
          title
          description
          tags
        }
        reasoning
      }
    }
  }
`);

const cashManagementStrategyOrgsDocument = graphql(`
  query AdminCashManagementStrategyOrgs {
    providers {
      ...CashManagementProvider
    }
    organisations {
      ...CashManagementOrg
    }
  }
`);

const updateCashManagementStrategyChecklistItemMutation = graphql(`
  mutation AdminUpdateCashManagementStrategyChecklistItemDetail(
    $input: UpdateCashManagementStrategyChecklistItemInput!
  ) {
    updateCashManagementStrategyChecklistItem(input: $input) {
      item {
        id
        status
        title
        description
        tags
      }
    }
  }
`);

function View() {
  const { id } = Route.useParams();
  if (!id) {
    throw new NotFoundError();
  }

  const apolloClient = useApolloClient();

  const { data } = useSuspenseQuery(cashManagementStrategyDetailDocument, {
    variables: { id },
  });

  const strategy = data.node;
  if (strategy && strategy.__typename !== "CashManagementStrategy") {
    throw new Error("expected type to be CashManagementStrategy");
  }

  const isGeneratingStrategy = strategy?.status === "GENERATING";
  const prevIsGeneratingStrategy = useRef(isGeneratingStrategy);

  const [pollInterval, setPollInterval] = useState<number | undefined>(
    isGeneratingStrategy ? 2000 : undefined,
  );

  let defaultAccordian: string;
  if (strategy) {
    defaultAccordian = isGeneratingStrategy ? "reasoning" : "strategy";
  } else {
    defaultAccordian = "inputs";
  }

  const [openAccordionItems, setOpenAccordionItems] = useState<string[]>([
    defaultAccordian,
  ]);

  useQuery(cashManagementStrategyDetailDocument, {
    pollInterval: pollInterval,
    skip: !pollInterval,
    variables: { id },
  });

  useEffect(() => {
    if (!isGeneratingStrategy && prevIsGeneratingStrategy.current) {
      setOpenAccordionItems(["strategy"]);
      setPollInterval(undefined);
      requestAnimationFrame(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
      });
    }
    prevIsGeneratingStrategy.current = isGeneratingStrategy;
  }, [isGeneratingStrategy]);

  const { data: orgData } = useSuspenseQuery(
    cashManagementStrategyOrgsDocument,
    strategy ? skipToken : undefined,
  );

  const [editingChecklistItem, setEditingChecklistItem] =
    useState<ChecklistItem | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [updateChecklistItem] = useMutation(
    updateCashManagementStrategyChecklistItemMutation,
  );
  const [publishStrategy, { loading: publishStrategyLoading }] = useMutation(
    graphql(`
      mutation PublishCashMgtStrat($id: ID!) {
        publishStrategy(input: { id: $id }) {
          strategy {
            id
            status
          }
        }
      }
`),
  );
  const [deleteCashManagementStrategy, { loading: deleteStrategyLoading }] =
    useMutation(
      graphql(`
        mutation DeleteCashMgtStrat($id: ID!) {
          deleteCashManagementStrategy(input: { id: $id }) {
            strategy {
              id
            }
          }
        }
  `),
    );
  const navigate = useNavigate();

  const title = strategy
    ? `Cash Management Strategy #${strategy.shortRef}`
    : "New Cash Management Strategy";

  useOpsTitle(title);

  const organisations = (orgData?.organisations?.slice() ?? []).sort(
    (left, right) => left.name.localeCompare(right.name),
  );

  const providers = orgData?.providers ?? [];

  const [selectedOrganisationId, setSelectedOrganisationId] = useState<
    string | undefined
  >(strategy?.organisation?.id);
  const selectedOrganisation =
    strategy?.organisation ??
    organisations.find(
      (organisation) => organisation.id === selectedOrganisationId,
    );

  const formRef = useRef<CashManagementStrategyFormHandle>(null);

  const checklistGroups = strategy
    ? CHECKLIST_GROUPS.map((group) => ({
        title: group.title,
        items: (strategy.checklist ?? []).filter(
          (
            item,
          ): item is ChecklistItem & {
            __typename: ChecklistTypename;
          } => "__typename" in item && item.__typename === group.typename,
        ),
      }))
    : [];

  const handleUpdateStatus = async (
    item: ChecklistItem,
    status: CashManagementStrategyChecklistItemStatus,
  ) => {
    void updateChecklistItem({
      variables: {
        input: {
          id: item.id,
          status,
        },
      },
      optimisticResponse: {
        __typename: "Mutation",
        updateCashManagementStrategyChecklistItem: {
          __typename: "UpdateCashManagementStrategyChecklistItemPayload",
          item: {
            ...item,
            status,
          },
        },
      },
      onError() {
        toast({
          autoHide: false,
          kind: "error",
          title: "Failed to update checklist item",
        });
      },
    });
  };

  const handleEditContent = async ({
    __typename,
    id: checklistItemId,
    title,
    description,
    tags,
    status,
  }: ChecklistItem) => {
    void updateChecklistItem({
      variables: {
        input: {
          id: checklistItemId,
          title,
          description,
          tags,
        },
      },
      optimisticResponse: {
        __typename: "Mutation",
        updateCashManagementStrategyChecklistItem: {
          __typename: "UpdateCashManagementStrategyChecklistItemPayload",
          item: {
            __typename,
            id: checklistItemId,
            title,
            description,
            tags,
            status,
          },
        },
      },
      onError() {
        toast({
          autoHide: false,
          kind: "error",
          title: "Failed to update checklist item",
        });
      },
    });

    setEditingChecklistItem(null);
  };

  const handlePublishStrategy = async () => {
    if (!strategy) {
      return;
    }

    try {
      await publishStrategy({
        variables: {
          id: strategy.id,
        },
      });

      await apolloClient.refetchQueries({
        include: [cashManagementStrategyDetailDocument],
      });

      toast({
        autoHide: true,
        kind: "success",
        title: "Strategy published",
      });
    } catch (error) {
      toast({
        autoHide: false,
        kind: "error",
        title: "Failed to publish strategy",
        description:
          error instanceof Error ? error.message : "Unknown error occurred",
      });
    }
  };

  const handleDeleteStrategy = async () => {
    if (!strategy) {
      return;
    }

    try {
      await deleteCashManagementStrategy({
        variables: {
          id: strategy.id,
        },
      });

      toast({
        autoHide: true,
        kind: "success",
        title: "Strategy deleted",
      });

      apolloClient.writeQuery({
        query: cashManagementStrategyDetailDocument,
        variables: { id },
        data: {
          __typename: "Query",
          node: null,
        },
      });

      setDeleteDialogOpen(false);
      void navigate({ to: "/cash-management-strategies" });
    } catch (error) {
      toast({
        autoHide: false,
        kind: "error",
        title: "Failed to delete strategy",
        description:
          error instanceof Error ? error.message : "Unknown error occurred",
      });

      setDeleteDialogOpen(false);
    }
  };

  return (
    <AdminPageContainer testId="admin-cash-management-strategy">
      <BreadcrumbArray
        stack={[
          {
            title: "Cash Management Strategies",
            to: "/cash-management-strategies",
          },
          {
            title: strategy ? (
              <span className="font-mono">#{strategy.shortRef}</span>
            ) : (
              "New"
            ),
          },
        ]}
      />
      <AdminPageHeader
        title={`${strategy ? "" : "New "}Cash Management Strategy`}
      />

      {isGeneratingStrategy && (
        <Alert
          kind="info"
          className="fixed top-6 right-6 left-6 z-30 mx-auto max-w-3xl border-2 bg-info-muted shadow-popover"
          title="Generating strategy"
          description={
            <div className="flex flex-row justify-between">
              <div className="flex flex-row gap-4">
                <div className="flex flex-col gap-1">
                  <Txt variant="detail-label">Generator stage</Txt>
                  <Txt variant="body">
                    {strategy.generatorStage
                      ? prettyPrintEnum(strategy.generatorStage)
                      : "-"}
                  </Txt>
                </div>
                <div className="flex flex-col gap-1">
                  <Txt variant="detail-label">Generation started</Txt>
                  <Txt variant="body">
                    {strategy.generationStartedAt ? (
                      <DateUI
                        date={strategy.generationStartedAt}
                        variant="DATETIME_TZ"
                      />
                    ) : (
                      "Not started"
                    )}
                  </Txt>
                </div>
                <div className="flex flex-col gap-1">
                  <Txt variant="detail-label">Expected completion</Txt>
                  <Txt variant="body">
                    {strategy.generationStartedAt ? (
                      <DateUI
                        date={DateTime.fromJSDate(strategy.generationStartedAt)
                          .plus({ minutes: 10 })
                          .toJSDate()}
                        variant="DATETIME_TZ"
                      />
                    ) : (
                      "-"
                    )}
                  </Txt>
                </div>
              </div>
            </div>
          }
          actions={
            <div className="flex items-center justify-center">
              <PrimarySpinner className="size-8 animate-pulse opacity-90" />
            </div>
          }
          iconNode={
            <div className="relative flex size-5 items-center justify-center">
              <span className="absolute size-5 animate-ping rounded-full bg-info opacity-50" />
              <span className="relative size-3 rounded-full bg-info" />
            </div>
          }
        />
      )}

      <div className="flex flex-col gap-6">
        {strategy ? (
          <div
            className={cn(
              isGeneratingStrategy ? "hidden" : "sticky",
              "top-0 z-50 w-full border-separator border-b bg-canvas/50",
            )}
          >
            <div className="my-4 flex w-full flex-row justify-between">
              <Button
                variant="destructive"
                onClick={() => setDeleteDialogOpen(true)}
              >
                Delete strategy
              </Button>
              <div className="flex flex-row gap-4">
                <FeedbackDialog
                  disabled={
                    strategy.status !== "DRAFT" && strategy.status !== "FAILED"
                  }
                  onSubmit={async (v) => {
                    await formRef.current?.regenerateWithFeedback(v);
                  }}
                />
                <Button
                  onClick={() => {
                    void handlePublishStrategy();
                  }}
                  disabled={strategy.status !== "DRAFT"}
                  loading={publishStrategyLoading}
                >
                  Publish strategy
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-6">
            <div className="flex max-w-sm flex-col gap-2">
              <Txt variant="name">Organisation</Txt>
              <Select
                value={selectedOrganisation?.id ?? ""}
                onValueChange={setSelectedOrganisationId}
                disabled={isGeneratingStrategy}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select organisation" />
                </SelectTrigger>
                <SelectContent>
                  {organisations.map((organisation) => (
                    <SelectItem key={organisation.id} value={organisation.id}>
                      <span className="flex flex-row items-center gap-1">
                        <Icon
                          size="4"
                          imageUrl={organisation.squareLogoUrl ?? undefined}
                          fallbackIcon={<PiBuildingOffice />}
                          bordered={false}
                        />
                        {organisation.name}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {selectedOrganisation && (
          <Accordion
            type="multiple"
            onValueChange={setOpenAccordionItems}
            value={openAccordionItems}
          >
            <AccordionItem value="inputs">
              <AccordionTrigger hideChevron={false}>
                Inputs to the strategy
              </AccordionTrigger>
              <AccordionContent
                forceMount
                className="data-[state=open]:block data-[state=closed]:hidden"
              >
                {strategy?.status !== "GENERATING" &&
                  strategy?.status !== "PUBLISHED" && (
                    <Card variant="panel" className="mb-6">
                      <Txt variant="page-title">
                        Upload cashflow file for {selectedOrganisation.name}
                      </Txt>
                      <CashflowUpload
                        organisationId={selectedOrganisation.id}
                        currency={selectedOrganisation.homeCurrency}
                        onApplyScenario={(scenario) => {
                          formRef.current?.applyScenario(scenario);
                        }}
                      />
                    </Card>
                  )}

                <Card variant="panel">
                  <CashManagementStrategyForm
                    organisation={selectedOrganisation}
                    providers={providers}
                    disabled={
                      strategy?.status === "GENERATING" ||
                      strategy?.status === "PUBLISHED"
                    }
                    strategyId={id}
                    onGeneratingStrategy={() => {
                      setOpenAccordionItems(["reasoning"]);
                      setPollInterval(2000);
                    }}
                    ref={formRef}
                  />
                </Card>
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="reasoning">
              <AccordionTrigger hideChevron={false}>
                Model Reasoning
              </AccordionTrigger>
              <AccordionContent>
                <Card>
                  <div className="flex flex-col gap-1">
                    <Txt variant="section-title">AI reasoning</Txt>
                    <Txt variant="description">
                      Reasoning text returned by the strategy agent.
                    </Txt>
                  </div>
                  <div className="mt-4 flex flex-col gap-1 rounded-md bg-muted p-2">
                    {strategy?.reasoning?.length ? (
                      strategy?.reasoning.map((r, i) => (
                        <Txt key={r + i} variant="body">
                          {r}
                        </Txt>
                      ))
                    ) : (
                      <Txt variant="body">No recorded model reasonings</Txt>
                    )}
                  </div>
                </Card>
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="strategy">
              <AccordionTrigger hideChevron={false}>Strategy</AccordionTrigger>
              <AccordionContent>
                {strategy ? (
                  <div className="flex flex-col gap-6">
                    <Card variant="panel" className="p-6">
                      <div className="mb-4 flex items-start justify-between gap-4">
                        <div className="flex flex-col gap-1">
                          <Txt variant="page-title">
                            {strategy.organisation?.name} strategy
                          </Txt>
                          <Txt variant="caption">#{strategy.shortRef}</Txt>
                        </div>
                        <div className="flex items-center gap-3">
                          <StatusUI status={strategy.status} />
                        </div>
                      </div>
                      <Suspense fallback={<></>}>
                        <CashManagementStrategyView
                          markdown={strategy.mdx ?? null}
                        />
                      </Suspense>
                    </Card>

                    {strategy?.checklist ? (
                      <CashManagementChecklist
                        editable
                        groups={checklistGroups}
                        onEditContent={setEditingChecklistItem}
                        onUpdateStatus={handleUpdateStatus}
                      />
                    ) : null}
                  </div>
                ) : (
                  <Txt variant="body">Generate a strategy to get started.</Txt>
                )}
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        )}
      </div>

      <EditChecklistItemDialog
        item={editingChecklistItem}
        onOpenChange={(open) => {
          if (!open) {
            setEditingChecklistItem(null);
          }
        }}
        onSave={handleEditContent}
      />

      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent
          requireIntentionalClosing={deleteStrategyLoading}
          className="max-w-xl"
        >
          <DialogHeader>
            <DialogTitle>Delete strategy #{strategy?.shortRef}</DialogTitle>
            <DialogDescription>
              This permanently deletes the cash management strategy and its
              checklist data. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogBody>
            <Txt variant="body">
              Delete this strategy and return to the strategy list.
            </Txt>
          </DialogBody>
          <DialogFooter>
            <Button
              variant="secondary"
              onClick={() => setDeleteDialogOpen(false)}
              disabled={deleteStrategyLoading}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              loading={deleteStrategyLoading}
              onClick={() => {
                void handleDeleteStrategy();
              }}
            >
              Confirm deletion
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminPageContainer>
  );
}

function EditChecklistItemDialog({
  item,
  onOpenChange,
  onSave,
}: {
  item: ChecklistItem | null;
  onOpenChange: (open: boolean) => void;
  onSave: (args: ChecklistItem) => Promise<void>;
}) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [tags, setTags] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!item) {
      setTitle("");
      setDescription("");
      setTags("");
      setIsSaving(false);
      return;
    }

    setTitle(item.title);
    setDescription(item.description);
    setTags(item.tags.join(", "));
  }, [item]);

  return (
    <Dialog open={item !== null} onOpenChange={onOpenChange}>
      <DialogContent requireIntentionalClosing={isSaving} className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Edit checklist item</DialogTitle>
          <DialogDescription>
            Update the checklist copy shown for this strategy step.
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <form
            className="flex flex-col gap-4"
            onSubmit={async (event) => {
              event.preventDefault();

              if (!item) {
                return;
              }

              setIsSaving(true);

              try {
                await onSave({
                  status: item.status,
                  __typename: item.__typename,
                  id: item.id,
                  title: title.trim(),
                  description: description.trim(),
                  tags: normaliseChecklistTags(tags),
                });
              } finally {
                setIsSaving(false);
              }
            }}
          >
            <label className="flex flex-col gap-2">
              <Txt variant="name">Title</Txt>
              <Input
                disabled={isSaving}
                onValueChange={setTitle}
                placeholder="Checklist item title"
                value={title}
              />
            </label>
            <label className="flex flex-col gap-2">
              <Txt variant="name">Description</Txt>
              <Textarea
                className="min-h-28"
                disabled={isSaving}
                onChange={(event) => setDescription(event.target.value)}
                placeholder="Checklist item description"
                value={description}
              />
            </label>
            <label className="flex flex-col gap-2">
              <Txt variant="name">Tags</Txt>
              <Input
                disabled={isSaving}
                onValueChange={setTags}
                placeholder="New, FX"
                value={tags}
              />
              <Txt tone="secondary" variant="caption">
                Enter tags separated by commas.
              </Txt>
            </label>
            <DialogFooter className="justify-end">
              <Button
                disabled={isSaving}
                onClick={() => onOpenChange(false)}
                type="button"
                variant="secondary"
              >
                Cancel
              </Button>
              <Button
                disabled={isSaving || !item || title.trim() === ""}
                type="submit"
              >
                Save changes
              </Button>
            </DialogFooter>
          </form>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}

function FeedbackDialog({
  disabled,
  onSubmit,
}: {
  disabled?: boolean;
  onSubmit: (feedback: string) => Promise<void>;
}) {
  const [userFeedback, setUserFeedback] = useState<string | undefined>();

  const [open, setOpen] = useState(false);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button disabled={disabled}>Regenerate</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Give feedback</DialogTitle>
          <DialogDescription>
            Give the model feedback on the strategy
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <Textarea
            value={userFeedback}
            onChange={(e) => setUserFeedback(e.currentTarget.value)}
          />
        </DialogBody>
        <DialogFooter>
          <Button
            variant="primary"
            disabled={!userFeedback}
            onClick={async () => {
              if (userFeedback) {
                await onSubmit(userFeedback);
                setOpen(false);
                setUserFeedback(undefined);
              }
            }}
          >
            Regenerate strategy
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
